/*
 * Validate-restaurant.ts
 * Description: Includes the declaration
 * of the function for validating a restaurant
 */

// Importing variables
import {
    InvalidExpensiveness, InvalidMinOrder, MissingAddress,
    MissingCeo, MissingDescription, MissingExpensiveness,
    MissingFirstName, MissingLegalName, MissingMinOrder,
    MissingNotes, MissingPhone, MissingTown,
    PhoneInvalid, PhoneRegex, TooLongAddress,
    TooLongCeo, TooLongFirstName, TooLongLegalName,
    TooLongNotes, TooLongPhone, MissingWorkingHours,
    MissingDeliveryPrices,
} from '@/ts/variables';

// Creating a Restaurant interface
export interface Restaurant {
    name: string;
    town_ekatte: string;
    description: string;
    phone: string;
    address: string;
    notes: string;
    min_order: string;
    expensiveness: number;
    legal_name: string;
    ceo: string;
    tags: string[];
    delivery_prices: Array<{
        gift: string[];
    }>;
    working_hours: Array<{
        start: string;
        end: string;
        day: string;
    }>;
    image: string;
    cover_image: string;
}

/**
 * Validating a restaurant
 *
 * @param {String[]} errors - a list of errors
 * @param {Restaurant} data - data to be validated
 */
export function validateRestaurant(errors: string[], data: Restaurant) {
    // Validating name
    if (data.name.length === 0) { errors.push(MissingFirstName); }
    if (data.name.length > 255) { errors.push(TooLongFirstName); }

    // Validating phone
    if (data.phone.length === 0) { errors.push(MissingPhone); }
    if (!PhoneRegex.test(data.phone)) { errors.push(PhoneInvalid); }
    if (data.phone.length > 255) { errors.push(TooLongPhone); }

    // Validating town
    if (data.town_ekatte.length === 0) { errors.push(MissingTown); }

    // Validating address
    if (data.address.length === 0) { errors.push(MissingAddress); }
    if (data.address.length > 255) { errors.push(TooLongAddress); }

    // Validating notes
    if (data.notes.length === 0) { errors.push(MissingNotes); }
    if (data.notes.length > 255) { errors.push(TooLongNotes); }

    // Validating minimal order value
    if (data.min_order.length === 0) { errors.push(MissingMinOrder); }
    if (Number(data.min_order) < 0) { errors.push(InvalidMinOrder); }

    // Validating expensiveness
    if (data.expensiveness === 0) { errors.push(MissingExpensiveness); }
    if (Number(data.expensiveness) < 1) { errors.push(InvalidExpensiveness); }

    // Validating working hours
    if (data.working_hours.length === 0) { errors.push(MissingWorkingHours); }

    // Validating delivery prices
    if (data.delivery_prices.length === 0) { errors.push(MissingDeliveryPrices); }

    // Validating legal name
    if (data.legal_name.length === 0) { errors.push(MissingLegalName); }
    if (data.legal_name.length > 255) { errors.push(TooLongLegalName); }

    // Validating ceo
    if (data.ceo.length === 0) { errors.push(MissingCeo); }
    if (data.ceo.length > 255) { errors.push(TooLongCeo); }

    // Validating description
    if (data.description.length === 0) { errors.push(MissingDescription); }
}
